// ============================================
// Lead Detail Page - Based on approved views/lead-detail.html
// ============================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { DetailPageSkeleton } from '@/components/skeletons';
import { LEAD_STATUS, LEAD_SOURCE, type LeadStatus, type LeadSource } from '@muraka/shared';
import { Loader2, ArrowLeft, Edit, Trash2, UserCheck } from 'lucide-react';

const statusConfig: Record<LeadStatus, { label: string; class: string }> = {
  new: { label: 'New', class: 'status-new' },
  contacted: { label: 'Contacted', class: 'status-contacted' },
  qualified: { label: 'Qualified', class: 'status-qualified' },
  unqualified: { label: 'Unqualified', class: 'status-unqualified' },
  converted: { label: 'Converted', class: 'status-converted' },
};

const sourceLabels: Record<LeadSource, string> = {
  website: 'Website',
  referral: 'Referral',
  cold_call: 'Cold Call',
  trade_show: 'Trade Show',
  partner: 'Partner',
  other: 'Other',
};

const avatarColors = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-red-100 text-red-700',
  'bg-pink-100 text-pink-700',
];

function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
}

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function formatFullDate(date: string): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function LeadDetailPage() {
  const { id } = useParams({ from: '/leads/$id' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [activeTab, setActiveTab] = useState<'details' | 'converted'>('details');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [convertModalOpen, setConvertModalOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    status: LEAD_STATUS.NEW as LeadStatus,
    source: LEAD_SOURCE.WEBSITE as LeadSource,
    notes: '',
  });

  // Convert form state
  const [convertForm, setConvertForm] = useState({
    createOpportunity: false,
    opportunityName: '',
    opportunityAmount: 0,
    opportunityCloseDate: '',
    accountName: '',
  });

  // Query lead
  const {
    data: lead,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => api.getLead(id),
    enabled: !!id,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) => api.updateLead(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setEditModalOpen(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteLead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate({ to: '/leads' });
    },
  });

  // Convert mutation
  const convertMutation = useMutation({
    mutationFn: () =>
      api.convertLead(id, {
        createNewAccount: true,
        accountName: convertForm.accountName || lead?.company || 'New Account',
        createOpportunity: convertForm.createOpportunity,
        opportunityName: convertForm.createOpportunity ? convertForm.opportunityName : undefined,
        opportunityAmount: convertForm.createOpportunity
          ? convertForm.opportunityAmount
          : undefined,
        opportunityCloseDate: convertForm.createOpportunity
          ? new Date(convertForm.opportunityCloseDate).toISOString()
          : undefined,
      }),
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setConvertModalOpen(false);
      navigate({ to: '/accounts/$id', params: { id: result.accountId } });
    },
  });

  // Open edit modal with current data
  const openEditModal = () => {
    if (lead) {
      setFormData({
        firstName: lead.firstName,
        lastName: lead.lastName,
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        title: lead.title || '',
        status: lead.status as LeadStatus,
        source: lead.source as LeadSource,
        notes: lead.notes || '',
      });
      setEditModalOpen(true);
    }
  };

  // Open convert modal
  const openConvertModal = () => {
    if (lead) {
      const closeDate = new Date();
      closeDate.setMonth(closeDate.getMonth() + 1);
      setConvertForm({
        createOpportunity: false,
        opportunityName: `${lead.company || 'New'} - Opportunity`,
        opportunityAmount: 0,
        opportunityCloseDate: closeDate.toISOString().split('T')[0],
        accountName: lead.company || '',
      });
      setConvertModalOpen(true);
    }
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleConvert = (e: React.FormEvent) => {
    e.preventDefault();
    convertMutation.mutate();
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">
          <DetailPageSkeleton />
        </div>
      </AppLayout>
    );
  }

  if (error || !lead) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <svg
              className="w-6 h-6 text-destructive"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <p className="text-destructive font-medium mb-2">Failed to load lead</p>
          <p className="text-sm text-muted-foreground mb-4">
            The lead may have been deleted or you don't have access.
          </p>
          <Button variant="outline" onClick={() => navigate({ to: '/leads' })}>
            Back to Leads
          </Button>
        </div>
      </AppLayout>
    );
  }

  const fullName = `${lead.firstName} ${lead.lastName}`;
  const status = statusConfig[lead.status as LeadStatus];
  const isConverted = lead.status === LEAD_STATUS.CONVERTED;
  const isUnqualified = lead.status === LEAD_STATUS.UNQUALIFIED;
  const canConvert = !isConverted && !isUnqualified;

  return (
    <AppLayout>
      {/* Lead Header Section */}
      <div className="bg-card border-b border-border -mx-4 -mt-4 lg:-mx-6 lg:-mt-6">
        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            {/* Lead Info */}
            <div className="flex items-start gap-4">
              <button
                onClick={() => navigate({ to: '/leads' })}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Leads</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mt-4">
            {/* Lead Info */}
            <div className="flex items-start gap-4">
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-semibold shrink-0 ${getAvatarColor(fullName)}`}
              >
                <span>{getInitials(lead.firstName, lead.lastName)}</span>
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-foreground">{fullName}</h1>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${status.class}`}
                  >
                    {status.label}
                  </span>
                </div>
                <p className="text-muted-foreground mt-1">
                  {lead.title && <span>{lead.title}</span>}
                  {lead.title && lead.company && <span> at </span>}
                  {lead.company && (
                    <span className="font-medium text-foreground">{lead.company}</span>
                  )}
                  {!lead.title && !lead.company && <span>No title or company</span>}
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                  {lead.email && (
                    <span className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <a href={`mailto:${lead.email}`} className="hover:text-primary">
                        {lead.email}
                      </a>
                    </span>
                  )}
                  {lead.phone && (
                    <span className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <a href={`tel:${lead.phone}`} className="hover:text-primary">
                        {lead.phone}
                      </a>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={openEditModal}>
                <Edit className="w-4 h-4" />
                Edit
              </Button>
              {canConvert && (
                <Button onClick={openConvertModal}>
                  <UserCheck className="w-4 h-4" />
                  Convert
                </Button>
              )}
              <Button variant="outline" size="icon" onClick={() => setDeleteModalOpen(true)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-card border-b border-border -mx-4 lg:-mx-6 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 lg:px-6">
          <nav className="flex gap-6 -mb-px">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Details
            </button>
            {isConverted && (
              <button
                onClick={() => setActiveTab('converted')}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'converted'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Converted Records
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-5xl mx-auto py-6">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content (2 columns) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Lead Information Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Lead Information</h2>
                  <button onClick={openEditModal} className="text-sm text-primary hover:underline">
                    Edit
                  </button>
                </div>
                <div className="p-6">
                  <dl className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm text-muted-foreground">Full Name</dt>
                      <dd className="mt-1 text-foreground">{fullName || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Job Title</dt>
                      <dd className="mt-1 text-foreground">{lead.title || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Email</dt>
                      <dd className="mt-1">
                        {lead.email ? (
                          <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
                            {lead.email}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Phone</dt>
                      <dd className="mt-1">
                        {lead.phone ? (
                          <a href={`tel:${lead.phone}`} className="text-primary hover:underline">
                            {lead.phone}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Company</dt>
                      <dd className="mt-1 text-foreground">{lead.company || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Source</dt>
                      <dd className="mt-1 text-foreground">
                        {sourceLabels[lead.source as LeadSource]}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Notes Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Notes</h2>
                  <button onClick={openEditModal} className="text-sm text-primary hover:underline">
                    Edit
                  </button>
                </div>
                <div className="p-6">
                  {lead.notes ? (
                    <p className="text-foreground whitespace-pre-wrap">{lead.notes}</p>
                  ) : (
                    <p className="text-muted-foreground italic">No notes added yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar (1 column) */}
            <div className="space-y-6">
              {/* Status Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">Status & Source</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Status</dt>
                    <dd className="mt-2">
                      <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${status.class}`}
                      >
                        {status.label}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Lead Source</dt>
                    <dd className="mt-1 text-foreground">
                      {sourceLabels[lead.source as LeadSource]}
                    </dd>
                  </div>
                </div>
              </div>

              {/* System Information Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">System Information</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Created</dt>
                    <dd className="mt-1 text-foreground">{formatFullDate(lead.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Last Modified</dt>
                    <dd className="mt-1 text-foreground">{formatFullDate(lead.updatedAt)}</dd>
                  </div>
                  {lead.convertedAt && (
                    <div>
                      <dt className="text-sm text-muted-foreground">Converted</dt>
                      <dd className="mt-1 text-foreground">{formatFullDate(lead.convertedAt)}</dd>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Converted Records Tab */}
        {activeTab === 'converted' && isConverted && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Account Created */}
            {lead.convertedAccountId && (
              <Link
                to="/accounts/$id"
                params={{ id: lead.convertedAccountId }}
                className="bg-card rounded-xl border border-border p-6 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">Account</span>
                </div>
                <h3 className="font-semibold text-foreground">{lead.company || 'New Account'}</h3>
                <p className="text-sm text-muted-foreground mt-1">Customer</p>
              </Link>
            )}

            {/* Contact Created */}
            {lead.convertedContactId && (
              <Link
                to="/contacts/$id"
                params={{ id: lead.convertedContactId }}
                className="bg-card rounded-xl border border-border p-6 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">Contact</span>
                </div>
                <h3 className="font-semibold text-foreground">{fullName}</h3>
                <p className="text-sm text-muted-foreground mt-1">{lead.title || 'Contact'}</p>
              </Link>
            )}

            {/* Opportunity Created (if applicable) */}
            {lead.convertedOpportunityId && (
              <Link
                to="/opportunities/$id"
                params={{ id: lead.convertedOpportunityId }}
                className="bg-card rounded-xl border border-border p-6 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-orange-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">Opportunity</span>
                </div>
                <h3 className="font-semibold text-foreground">{lead.company} - Opportunity</h3>
                <p className="text-sm text-muted-foreground mt-1">Qualification Stage</p>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Edit Lead Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <DialogContent onClose={() => setEditModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="p-6 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="First Name"
                  required
                  value={formData.firstName}
                  onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                />
                <Input
                  label="Last Name"
                  required
                  value={formData.lastName}
                  onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Smith"
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                />
                <Input
                  label="Phone"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
                <Input
                  label="Company"
                  value={formData.company}
                  onChange={e => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Acme Inc."
                />
                <Input
                  label="Job Title"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="CEO, Director, etc."
                />
                <Select
                  label="Status"
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value as LeadStatus })}
                  disabled={isConverted}
                >
                  {Object.entries(statusConfig)
                    .filter(([value]) => value !== 'converted' || isConverted)
                    .map(([value, config]) => (
                      <option key={value} value={value}>
                        {config.label}
                      </option>
                    ))}
                </Select>
                <Select
                  label="Source"
                  required
                  value={formData.source}
                  onChange={e => setFormData({ ...formData, source: e.target.value as LeadSource })}
                >
                  {Object.entries(sourceLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              <Textarea
                label="Notes"
                rows={3}
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add any relevant notes..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert Lead Modal */}
      <Dialog open={convertModalOpen} onClose={() => setConvertModalOpen(false)}>
        <DialogContent onClose={() => setConvertModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Convert Lead</DialogTitle>
            <DialogDescription>
              Converting this lead will create an Account and Contact
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConvert}>
            <div className="p-6">
              {/* Lead Summary */}
              <div className="p-4 rounded-lg bg-muted/50 mb-6">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${getAvatarColor(fullName)}`}
                  >
                    <span>{getInitials(lead.firstName, lead.lastName)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{fullName}</p>
                    <p className="text-sm text-muted-foreground">{lead.company || 'No company'}</p>
                  </div>
                </div>
              </div>

              {/* Conversion Details */}
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 rounded-lg border border-border">
                  <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center shrink-0">
                    <svg
                      className="w-4 h-4 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-foreground">Account will be created</p>
                    <Input
                      className="mt-2"
                      value={convertForm.accountName}
                      onChange={e =>
                        setConvertForm({ ...convertForm, accountName: e.target.value })
                      }
                      placeholder="Account name"
                    />
                  </div>
                  <svg
                    className="w-5 h-5 text-green-500 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg border border-border">
                  <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center shrink-0">
                    <svg
                      className="w-4 h-4 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-foreground">Contact will be created</p>
                    <p className="text-xs text-muted-foreground">
                      {fullName} ({lead.email})
                    </p>
                  </div>
                  <svg
                    className="w-5 h-5 text-green-500 ml-auto shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                {/* Opportunity Option */}
                <div className="p-3 rounded-lg border border-border">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={convertForm.createOpportunity}
                      onChange={e =>
                        setConvertForm({ ...convertForm, createOpportunity: e.target.checked })
                      }
                      className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
                    />
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-8 h-8 rounded bg-orange-100 flex items-center justify-center shrink-0">
                        <svg
                          className="w-4 h-4 text-orange-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-sm text-foreground">Create an Opportunity</p>
                        <p className="text-xs text-muted-foreground">Start tracking a deal</p>
                      </div>
                    </div>
                  </label>

                  {convertForm.createOpportunity && (
                    <div className="mt-4 pt-4 border-t border-border space-y-4">
                      <Input
                        label="Opportunity Name"
                        value={convertForm.opportunityName}
                        onChange={e =>
                          setConvertForm({ ...convertForm, opportunityName: e.target.value })
                        }
                        placeholder={`${lead.company} - Opportunity`}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="Amount"
                          type="number"
                          value={convertForm.opportunityAmount}
                          onChange={e =>
                            setConvertForm({
                              ...convertForm,
                              opportunityAmount: Number(e.target.value),
                            })
                          }
                          placeholder="0"
                        />
                        <Input
                          label="Close Date"
                          type="date"
                          value={convertForm.opportunityCloseDate}
                          onChange={e =>
                            setConvertForm({ ...convertForm, opportunityCloseDate: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConvertModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={convertMutation.isPending}>
                {convertMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Convert Lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogContent onClose={() => setDeleteModalOpen(false)} className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-destructive"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              Delete Lead?
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">{fullName}</span>? All associated data
              will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
